/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import apiClient from '../client';
import { API_CONFIG, API_ENDPOINTS } from '../config';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  User,
} from '../types';

class AuthService {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    );
    
    // Save tokens
    if (response.data.token && response.data.refreshToken) {
      apiClient.saveTokens(response.data.token, response.data.refreshToken);
      
      // Save user data
      localStorage.setItem(API_CONFIG.USER_KEY, JSON.stringify(response.data.user));
    }
    
    return response.data;
  }

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      data
    );
    
    // Save tokens
    if (response.data.token && response.data.refreshToken) {
      apiClient.saveTokens(response.data.token, response.data.refreshToken);
      
      // Save user data
      localStorage.setItem(API_CONFIG.USER_KEY, JSON.stringify(response.data.user));
    }
    
    return response.data;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } finally {
      // Clear tokens and user data regardless of API response
      apiClient.clearTokens();
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>(
      API_ENDPOINTS.AUTH.REFRESH,
      { refreshToken }
    );
    
    // Update tokens
    if (response.data.token && response.data.refreshToken) {
      apiClient.saveTokens(response.data.token, response.data.refreshToken);
    }
    
    return response.data;
  }

  /**
   * Forgot password - Send reset email
   */
  async forgotPassword(email: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
  }

  /**
   * Reset password
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      token,
      newPassword,
    });
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(API_CONFIG.USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(API_CONFIG.TOKEN_KEY);
    return !!token;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;

